import { type NodeOrganization, type Pagination, createNodeOrganization } from 'models';
import { useInfiniteQuery } from 'query';
import type { PaginationData } from 'common/services';
import { getNodeOrganizations } from '../services';
import { getNodeOrganizationsInfiniteQueryKey } from './keys';

export type NodeOrganizationsListFilter = {
  admin: string;
  name?: string;
};

type EventsQueryParams = {
  filter: NodeOrganizationsListFilter;
  options?: { enabled?: boolean };
  pagination: Omit<Pagination, 'position'>;
};

type NodeOrganizationsPageParameter = {
  skip: number;
};

export const useNodeOrganizationsInfiniteQuery = ({ filter, options, pagination }: EventsQueryParams) =>
  useInfiniteQuery<PaginationData<NodeOrganization>, NodeOrganizationsPageParameter>({
    async queryFn({ pageParam }) {
      const { data: nodeOrganizations } = await getNodeOrganizations({
        config: {
          params: {
            $limit: pagination.pageSize,
            $skip: pageParam.skip,
            admin: filter.admin,
            name: filter.name ? { $startsWith: filter.name } : undefined,
          },
        },
      });

      return {
        data: nodeOrganizations.data.map((organization) => createNodeOrganization(organization)),
        limit: nodeOrganizations.limit,
        skip: nodeOrganizations.skip,
        total: nodeOrganizations.total,
      };
    },
    initialPageParam: { skip: 0 },
    getNextPageParam: (lastPage) =>
      lastPage.skip + lastPage.data.length < lastPage.total ? { skip: lastPage.skip + lastPage.limit } : undefined,
    queryKey: getNodeOrganizationsInfiniteQueryKey({
      filter,
      pagination,
    }),
    throwOnError: true,
    ...options,
  });
